import { useMemo } from 'react';
import { cx } from '@platform/core/lib/cx';
import type { IMarkdownViewProps } from './IMarkdownViewProps';
import { mdBlocks } from './mdBlocks';
import { MdInlines } from './MdInlines';
import { MdQuery } from './MdQuery';
import './markdown.css';

/**
 * Стаття Markdown (UIUX/field-types/rich-text; прототип mdToHtml): заголовки, абзаци, списки, цитати, код, лінії, таблиці, кольоровий
 * текст, виділення, посилання на записи й живі запити (```query — таблиця записів з API). Без HTML-вставок — лише елементи React.
 */
export function MarkdownView({ text, ws, emptyText, className }: IMarkdownViewProps) {
  const blocks = useMemo(() => mdBlocks(text), [text]);
  if (!blocks.length) return emptyText ? <p className={cx('md', 'md--empty', className)}>{emptyText}</p> : null;
  return (
    <div className={cx('md', className)}>
      {blocks.map((b, i) => {
        switch (b.kind) {
          case 'heading': return b.level! <= 2
            ? <h3 key={i} className="md__h md__h--lg"><MdInlines nodes={b.inlines} ws={ws} /></h3>
            : <h4 key={i} className="md__h"><MdInlines nodes={b.inlines} ws={ws} /></h4>;
          case 'ul': return <ul key={i} className="md__list">{b.items!.map((it, k) => <li key={k}><MdInlines nodes={it} ws={ws} /></li>)}</ul>;
          case 'ol': return <ol key={i} className="md__list md__list--ol">{b.items!.map((it, k) => <li key={k}><MdInlines nodes={it} ws={ws} /></li>)}</ol>;
          case 'quote': return <blockquote key={i} className="md__quote"><MdInlines nodes={b.inlines} ws={ws} /></blockquote>;
          case 'code': return <pre key={i} className="md__pre">{b.text}</pre>;
          case 'query': return <MdQuery key={i} lines={b.lines ?? []} ws={ws} />;
          case 'hr': return <hr key={i} className="md__hr" />;
          case 'table': return (
            <div key={i} className="md__table-wrap">
              <table className="md__table">
                <thead><tr>{b.head!.map((c, k) => <th key={k}><MdInlines nodes={c} ws={ws} /></th>)}</tr></thead>
                <tbody>{b.rows!.map((r, k) => <tr key={k}>{r.map((c, j) => <td key={j}><MdInlines nodes={c} ws={ws} /></td>)}</tr>)}</tbody>
              </table>
            </div>
          );
          default: return <p key={i} className="md__p"><MdInlines nodes={b.inlines} ws={ws} /></p>;
        }
      })}
    </div>
  );
}
